import { Component, computed, effect, inject, signal, Signal } from '@angular/core';
import { FacetFormComponent } from 'app/my-history/shared/facet-form.component';
import { ButtonComponent } from 'app/shared/button/button.component';
import { IIclData, IStory, IStoryPayload, StoryForm, StoryUnit } from '../story';
import { FormArray, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { StoryService } from '../story.service';
import { NgFor } from '@angular/common';
import { TextButtonComponent } from 'app/shared/button/text-button/text-button.component';
import { InlineDialogTitleComponent } from 'app/shared/dialog/inline-dialog/inline-dialog-title/inline-dialog-title.component';
import { FileUploadComponent } from 'app/shared/form/file-upload/file-upload.component';
import { FormGridComponent } from 'app/shared/form/form-grid/form-grid.component';
import { InputComponent } from 'app/shared/form/input/input.component';
import { Facet, FacetAlias } from 'app/my-history/facet/facet';
import { FacetSelectionComponent } from 'app/my-history/facet/facet-selection/facet-selection.component';
import { StoryStatus } from '../story-status';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChapterService } from 'app/my-history/chapter/chapter.service';
import { FacetModel } from 'app/my-history/facet/facet.model';
import { FacetGridCompactContainerComponent } from 'app/my-history/facet/facet-grid-compact-container/facet-grid-compact-container.component';
import { provideIcons } from '@ng-icons/core';
import { heroChevronRight, heroSpeakerWave } from '@ng-icons/heroicons/outline';
import { FileUploadPreviewComponent } from 'app/shared/form/file-upload/file-upload-preview/file-upload-preview.component';
import { StateType, VoiceRecordingComponent } from '../../../shared/form/voice-recording/voice-recording.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { StoryPreviewComponent } from '../story-preview/story-preview.component';
import { FormUtils } from 'app/shared/form/form-utils';
import { HttpUtils } from 'app/shared/http-utils';
import { DialogService } from 'app/shared/dialog/dialog.service';
import { DialogComponent } from 'app/shared/dialog/dialog.component';
import { IPreviewUrls } from 'app/shared/form/file.model';
import { forkJoin } from 'rxjs';
import { DatePickerType, IDateData } from 'app/shared/date.const';
import { DatepickerComponent } from 'app/shared/datepicker/datepicker.component';
import { AuthService } from '../../../auth/auth.service';
import { MatDialogRef } from '@angular/material/dialog';
import { StoryFilterService } from '../story-filter.service';
import { ShareStoryComponent } from '../share-story/share-story.component';

@Component({
  selector: 'app-story-form',
  standalone: true,
  imports: [
    FormGridComponent,
    ReactiveFormsModule,
    InputComponent,
    FileUploadComponent,
    ButtonComponent,
    TextButtonComponent,
    NgFor,
    InlineDialogTitleComponent,
    FacetSelectionComponent,
    FacetGridCompactContainerComponent,
    FileUploadPreviewComponent,
    VoiceRecordingComponent,
    MatCheckboxModule,
    StoryPreviewComponent,
    FormsModule,
    DialogComponent,
    DatepickerComponent,
    DatepickerComponent,
    DatepickerComponent,
    ShareStoryComponent,
  ],
  templateUrl: './story-form.component.html',
  styleUrls: ['./story-form.component.scss'],
  providers: [ChapterService, provideIcons({ heroChevronRight, heroSpeakerWave })],
})
export class StoryFormComponent extends FacetFormComponent<IStory, StoryForm, IStoryPayload> {
  override facetCrudService = inject(StoryService);
  private dialog = inject(DialogService);
  private storyService = inject(StoryService);
  user = inject(AuthService).user;
  private dialogRef = inject(MatDialogRef<StoryFormComponent>);
  private storyFilterService = inject(StoryFilterService);

  private story: IStory | undefined;
  private id: string | undefined;
  private imageToRemove: string[] = [];

  override fileApiUrl: string = HttpUtils.getApiUrl('/files/stories');
  private audioApiUrl: string = HttpUtils.getApiUrl('/files/audio');

  isDraft = false;
  textContent: string = '';
  maxLengthTextarea: number = 4000;

  storyAcl = signal<IIclData>({
    friends: [],
    acquaintance: false,
    communities: null,
  });

  shareWithFriendsDisplay = signal(false);
  audioState = signal<StateType>('idle');
  previewUrls = signal<IPreviewUrls[]>([]);
  isPreview = signal(false);
  selectedFacetModels = signal<FacetModel[]>([]);
  isSelectFacets = signal(false);
  validateDatePicker = signal(false);
  transcript = signal('');
  isNewAudioRecorded = signal(false);
  facetModels = this.chapterService.chapters;
  audioUrl = signal<string | undefined>(undefined);
  isUploaded = signal(false);
  selectedPicker = signal<DatePickerType | null>(null);
  userYears = computed(() => {
    const userDate = new Date(this.user().birthDate);
    const today = new Date();
    return today.getFullYear() - userDate.getFullYear();
  });
  dateData: IDateData | undefined;

  override selected: Signal<IStory | null> = this.facetCrudService.selectedFacet;
  override selectedImage: Signal<string | null | string[]> = this.facetCrudService.selectedFacetImage;

  formArray = computed(() => this.form().get('facets') as FormArray);
  formGroup = computed(() => this.formArray().at(0));
  selectedFacetTypes = computed(() => {
    const selectedFacetType = this.selectedFacetType();
    if (!selectedFacetType) return [];
    return [selectedFacetType];
  });
  selectedFacetTypeName = computed(
    () => `${this.selectedFacetType()?.alias === FacetAlias.education ? 'an' : 'a'} ${this.selectedFacetType()?.name}`,
  );
  defaultSelectedFacet = computed(() => this.selectedFacetType()?.alias);
  isNextButton = computed(() => this.isSelectFacets() || !this.selectedFacetModels().length);
  submitButtonText = computed(() => {
    let buttonTitle = this.isNextButton() ? 'Next' : this.isPreview() ? 'Publish' : 'Preview';

    // in edit mode preview is absent
    if (!this.isNextButton() && this.mode() === 'Edit') {
      buttonTitle = 'Save Changes';
    }

    return buttonTitle;
  });

  constructor() {
    super();

    effect(() => {
      if (this.facetId()) {
        this.facetCrudService.addStoryAcl(this.facetId() as string, this.storyAcl()).subscribe();
      }
    });
  }

  onDateChange(response: IDateData) {
    if (response) {
      if (response.type) {
        this.selectedPicker.set(response.type as DatePickerType);
      }

      if (response.dateComponents) {
        this.formGroup()?.get('dateComponents')?.setValue(response.dateComponents);
      } else {
        this.formGroup()?.get('dateComponents')?.setValue(null);
      }
    }

    this.dateData = response;
  }

  setAudioState(state: StateType) {
    if (state === 'denied') {
      this.audioState.set(state);
    }
  }

  onRemoveImage(index: number) {
    const imagesArray = this?.formArray()?.at(0)?.get('imageFile')?.value || [];

    if (imagesArray.length) {
      const image = imagesArray[index];
      const newImages = imagesArray.filter((_: IPreviewUrls | File, i: number) => index !== i);
      const previewUrls = this.previewUrls();

      this.previewUrls.set(previewUrls.filter((_: IPreviewUrls, i: number) => index !== i));

      if (image.url) {
        this.imageToRemove.push(image.name);
      }

      this.formArray().at(0).get('imageFile')?.setValue(newImages);
    }
  }

  setPreviewUrl(previewUrls: IPreviewUrls[]) {
    if (!previewUrls || typeof previewUrls === 'string') return;

    if (previewUrls.length) {
      this.previewUrls.set(previewUrls);
    }
  }

  updateDescription(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.textContent = target?.value || '';
  }

  override createFormGroup(facet: IStory | null): FormGroup {
    let id;
    let title;
    let content;
    let units;
    let status;
    let dateComponents;
    const category = this.storyFilterService.selectedFacet();

    // pre set data if stories by facet opened
    if (category) {
      dateComponents = {
        year: new Date(category.start).getFullYear(),
      };

      this.dateData = {
        dateComponents,
        type: DatePickerType.YEAR,
      };

      units = [category.lense.id];
    }

    if (facet) {
      id = facet.id;
      title = facet.title;
      content = facet.content;
      units = facet.units.map((u) => u.lenseId);
      status = facet.status;
      dateComponents = facet.dateComponents;

      this.story = facet;
      this.id = facet.id;
      this.textContent = facet.content;

      this.dateData = {
        dateComponents,
        type: DatePickerType.YEAR,
      };

      if (facet.dateComponents.month !== undefined) {
        this.dateData.type = DatePickerType.MONTH;
      }

      if (facet.dateComponents.day) {
        this.dateData.type = DatePickerType.DAY;
      }
    }

    queueMicrotask(() => {
      if (category) {
        this.chapterService.setSelectedFacetType(category.lense);
        this.chapterService
          .getAll(category.lense.id, true)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((models) => {
            const selectedModel = models.find((model) => model.id === category.id);
            this.selectedFacetModels.set([selectedModel as FacetModel]);
          });
      }

      if (facet) {
        this.setPreviewUrl(this.facetCrudService.getStoryImages(facet as IStory));
        this.formArray().at(0)?.get('imageFile')?.setValue(this.previewUrls());

        if (facet.audio && facet.audio.data && facet.audio.data.length) {
          this.audioUrl.set(facet.audio.data[0].url);
          this.isUploaded.set(true);

          this.formArray().at(0)?.get('audio')?.setValue(this.audioUrl());
        }

        facet.units.forEach((u) => {
          this.chapterService.selectedFacetTypeId.set(u.lenseId);
          this.chapterService
            .getAll(u.lenseId, true)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((models) => {
              const selectedModel = models.find((model) => model.id === u.id);
              this.selectedFacetModels.set([selectedModel as FacetModel]);
            });
        });
      }
    });

    return this.fb.nonNullable.group({
      id: [id],
      title: [title, Validators.required],
      content: [content, [Validators.required, Validators.maxLength(this.maxLengthTextarea)]],
      units: [units],
      status: [status],
      dateComponents: [dateComponents, Validators.required],
      imageFile: [],
      audio: [],
    });
  }

  override getSubmitPayload(facet: StoryForm): IStoryPayload {
    const status = this.isDraft ? StoryStatus.draft : StoryStatus.publish;
    const { title, content, units, start, dateComponents } = facet;
    const id = facet.id ? facet.id : undefined;
    const unitsToSave: StoryUnit[] = this.selectedFacetModels().length
      ? units.map((u) => ({ lenseId: u, id: this.selectedFacetModels()[0]?.id }))
      : [];

    return {
      id,
      title,
      content,
      units: unitsToSave,
      status,
      start,
      dateComponents,
    };
  }

  override onSubmitted(facet: IStory): void {
    super.onSubmitted(facet);

    this.dialogRef.close(facet);
    this.storyService.getStoriesData();
  }

  override submitFormGroup(facet: StoryForm) {
    super.submitFormGroup(facet);
  }

  override onSubmit(): void {
    if (this.isDraft) {
      this.remove();
      super.onSubmit();
      return;
    }

    this.validateDatePicker.set(true);

    const { formValidityCheck } = FormUtils;
    if (!formValidityCheck(this.form())) {
      return;
    }

    if (this.selectedFacetModels().length) {
      if (this.isSelectFacets()) {
        this.back();
      } else if (!this.isPreview() && this.mode() === 'Create') {
        this.preview();
      } else {
        this.isDraft = this.story?.status === StoryStatus.draft;
        this.remove();
        super.onSubmit();
        this.isDraft = false;
      }
    } else {
      this.next();
    }
  }

  convertVoiceToText() {
    const contentControl = this.formGroup()?.get('content');
    if (!contentControl) return;

    contentControl.setValue(`${contentControl.value ? contentControl.value + ' ' : ''}${this.transcript()}`);

    this.textContent = contentControl.value;

    this.form().updateValueAndValidity();
    this.transcript.set('');
    this.isNewAudioRecorded.set(false);
  }

  onTranscriptChange(transcript: string) {
    this.transcript.set(transcript);
  }

  onAudioRecorded(recorded: boolean) {
    this.isNewAudioRecorded.set(recorded);
    // this.audioUrl.set((this.formGroup().get('audio')?.value))
  }

  preview() {
    this.isPreview.set(true);
  }

  next() {
    this.isSelectFacets.set(true);
  }

  back() {
    this.isSelectFacets.set(false);
    this.isPreview.set(false);
  }

  backToPreview() {
    this.isPreview.set(true);
    this.shareWithFriendsDisplay.set(false);
  }

  updateAcl(acl: IIclData) {
    this.storyAcl.set(acl);
    this.facetCrudService.setAcl(this.storyAcl());
  }

  toogleFacetModel(facetModel: FacetModel) {
    this.selectedFacetModels.set([facetModel]);
  }

  deleteFacetModel() {
    this.selectedFacetModels.set([]);
    this.isSelectFacets.set(false);
    this.chapterService.setSelectedFacetType(null);
  }

  onSelectedFacetChanged(facet: Facet): void {
    this.chapterService.setSelectedFacetType(facet);
    this.chapterService.getAll(facet.id, true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  onSaveDraft() {
    this.isDraft = true;
    this.onSubmit();
    this.isDraft = false;
  }

  onRemoveAudio(uploaded: boolean) {
    this.isUploaded.set(uploaded);
  }

  remove() {
    let storyAudio;
    const currentAudio = this.formArray().at(0)?.get('audio')?.value;

    if (this?.story?.audio?.data.length) {
      storyAudio = this?.story?.audio?.data[0].url;
    }

    if (storyAudio && storyAudio !== currentAudio) {
      this.filesService.removeFile(this.audioApiUrl, this.story?.id as string).subscribe();
    }

    if (this.imageToRemove.length) {
      forkJoin(
        this.imageToRemove.map((imgName) => {
          return this.facetCrudService.removeImage(this.fileApiUrl, imgName, this.id as string);
        }),
      ).subscribe(() => {
        this.imageToRemove = [];
      });
    }
  }

  showShareWithFriendsScreen(display: boolean) {
    this.shareWithFriendsDisplay.set(display);
    this.isPreview.set(false);
  }
}
